import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { 
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { 
  Home,
  Image as ImageIcon,
  Mic,
  FileText,
  User,
  CreditCard,
  Settings,
  LogOut,
  Crown,
  Sparkles,
  BarChart3,
  Library,
  Zap,
  Wand2,
  History,
  BookTemplate
} from "lucide-react";
import { NavLink, useLocation } from "react-router-dom";

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const { open } = useSidebar();
  const location = useLocation();
  const currentPath = location.pathname;

  const handleNavClick = (url: string) => {
    const [basePath, query] = url.split('?');
    if (query) {
      const params = new URLSearchParams(query);
      const tab = params.get('tab');
      if (tab) {
        // Navigate to dashboard and trigger tab change
        window.location.href = `/dashboard?tab=${tab}`;
        return;
      }
    }
    window.location.href = url;
  };

  const mainNavItems = [
    { title: "Dashboard", url: "/dashboard", icon: Home },
    { title: "Image Generation", url: "/dashboard?tab=image", icon: ImageIcon },
    { title: "Voice Generation", url: "/dashboard?tab=voice", icon: Mic },
    { title: "Script Generation", url: "/dashboard?tab=script", icon: FileText },
    { title: "Image Enhancer", url: "/dashboard?tab=enhance", icon: Wand2 },
    { title: "Templates", url: "/dashboard?tab=templates", icon: BookTemplate },
    { title: "History", url: "/dashboard?tab=history", icon: History },
    { title: "Asset Library", url: "/dashboard?tab=library", icon: Library },
  ];

  const accountNavItems = [
    { title: "Profile", url: "/profile", icon: User },
    { title: "Billing & Plans", url: "/billing", icon: CreditCard },
    { title: "Settings", url: "/settings", icon: Settings },
  ];

  if (user?.isAdmin) {
    accountNavItems.push({ title: "Admin Panel", url: "/admin", icon: BarChart3 });
  }

  const isActive = (path: string) => {
    if (path === '/dashboard' && currentPath === '/dashboard' && !location.search) return true;
    if (path.includes('?tab=') && location.search.includes(path.split('?tab=')[1])) return true;
    return currentPath === path;
  };

  const getNavClassName = (path: string) => {
    const baseClasses = "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 hover:bg-accent/50 hover:text-accent-foreground group";
    return isActive(path) 
      ? `${baseClasses} bg-gradient-to-r from-purple-500/20 to-blue-500/20 text-foreground shadow-sm border border-purple-500/30` 
      : `${baseClasses} text-muted-foreground hover:text-foreground`;
  };

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'pro':
        return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg';
      case 'business':
      case 'team':
        return 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg';
      case 'starter':
        return 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const getPlanIcon = (plan: string) => {
    switch (plan) {
      case 'pro':
        return <Crown className="w-3 h-3 mr-1" />;
      case 'business':
      case 'team':
        return <Crown className="w-3 h-3 mr-1" />;
      case 'starter':
        return <Sparkles className="w-3 h-3 mr-1" />;
      default:
        return null;
    }
  };

  return (
    <Sidebar
      className={`transition-all duration-300 ease-in-out ${!open ? "w-16" : "w-72"} bg-background/80 backdrop-blur-md border-r border-border/20`}
      collapsible="icon"
    >
      <SidebarContent className="flex flex-col h-full">
        {/* User Profile Section */}
        <div className="p-6 border-b border-border/20">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="w-10 h-10 ring-2 ring-primary/20 shadow-lg">
                <AvatarImage src={user?.avatarUrl} />
                <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-background"></div>
            </div>
            
            {open && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">
                  {user?.name || 'User'}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={`text-xs px-2 py-0.5 ${getPlanBadgeColor(user?.plan || 'free')}`}>
                    {getPlanIcon(user?.plan || 'free')}
                    {(user?.plan || 'free').toUpperCase()}
                  </Badge>
                </div>
                <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                  <Zap className="w-3 h-3" />
                  <span>{user?.creditsUsed || 0}/{user?.creditsLimit || 5} used</span>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Main Navigation */}
        <SidebarGroup className="flex-1 px-3 py-4">
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {open ? 'Main Menu' : ''}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <button 
                      onClick={() => handleNavClick(item.url)}
                      className={getNavClassName(item.url)}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {open && <span>{item.title}</span>}
                      {isActive(item.url) && open && (
                        <div className="ml-auto w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                      )}
                    </button>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Account Section */}
        <SidebarGroup className="px-3 py-4 border-t border-border/20 bg-muted/30">
          <SidebarGroupLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
            {open ? 'Account' : ''}
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu className="space-y-1">
              {accountNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink 
                      to={item.url} 
                      className={getNavClassName(item.url)}
                    >
                      <item.icon className="w-5 h-5 flex-shrink-0" />
                      {open && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
              
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <button 
                    onClick={signOut}
                    className="flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-all duration-200 hover:bg-red-500/10 hover:text-red-600 text-muted-foreground w-full"
                  >
                    <LogOut className="w-5 h-5 flex-shrink-0" />
                    {open && <span>Sign Out</span>}
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}